// src/components/ExcludeFolderTree.tsx
//
// Collapsible tree of the dropped folder's subfolders, with a checkbox per
// folder to exclude it (and everything under it) from folder mirroring.
//
// Nodes are either eager (children known up front — ZIP / input picker) or
// lazy (children listed on first expand — drag-and-drop, so cloud drives like
// Box are only asked about folders the user actually opens).

import React, { useState } from "react";
import { listSubfolders } from "../utils/folderMirror";

export type TreeNode = {
  name: string;
  path: string;                               // relative path, original case
  children?: TreeNode[];                      // eager
  loadChildren?: () => Promise<TreeNode[]>;   // lazy
};

type Props = {
  roots: TreeNode[];
  excluded: Set<string>;           // lowercase paths currently excluded
  onToggle: (path: string) => void;
  isNewFolder?: (path: string) => boolean; // not yet in SharePoint
};

/** Build an eager tree from flat "A/B/C" paths (intermediate nodes created as needed). */
export function treeFromPaths(paths: string[]): TreeNode[] {
  const root: TreeNode = { name: "", path: "", children: [] };
  const byPath = new Map<string, TreeNode>([["", root]]);

  const sorted = [...paths].sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
  for (const p of sorted) {
    let parent = root;
    let cur = "";
    for (const seg of p.split("/").filter(Boolean)) {
      cur = cur ? `${cur}/${seg}` : seg;
      let node = byPath.get(cur);
      if (!node) {
        node = { name: seg, path: cur, children: [] };
        byPath.set(cur, node);
        parent.children!.push(node);
      }
      parent = node;
    }
  }
  return root.children!;
}

/** Build a lazy tree from dropped FileSystemEntries — only directories, listed on expand. */
export function treeFromEntries(entries: any[]): TreeNode[] {
  const nodeFor = (entry: any, prefix: string): TreeNode => {
    const path = prefix ? `${prefix}/${entry.name}` : entry.name;
    return {
      name: entry.name,
      path,
      loadChildren: async () => (await listSubfolders(entry)).map((e) => nodeFor(e, path)),
    };
  };
  return entries
    .filter((e) => e.isDirectory)
    .sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true }))
    .map((e) => nodeFor(e, ""));
}

export default function ExcludeFolderTree({ roots, excluded, onToggle, isNewFolder }: Props) {
  if (roots.length === 0) {
    return <div style={{ fontSize: 12, color: "#6b7280", padding: 8 }}>No subfolders in the dropped folder.</div>;
  }

  return (
    <div style={{ fontFamily: "monospace", fontSize: 12 }}>
      {roots.map((n) => (
        <Row key={n.path} node={n} depth={0} excluded={excluded} inherited={false} onToggle={onToggle} isNewFolder={isNewFolder} />
      ))}
    </div>
  );
}

function Row({
  node, depth, excluded, inherited, onToggle, isNewFolder,
}: {
  node: TreeNode;
  depth: number;
  excluded: Set<string>;
  inherited: boolean; // an ancestor is excluded
  onToggle: (path: string) => void;
  isNewFolder?: (path: string) => boolean;
}) {
  const [open, setOpen] = useState(false);
  const [loaded, setLoaded] = useState<TreeNode[] | null>(node.children ?? null);
  const [loading, setLoading] = useState(false);

  const self = excluded.has(node.path.toLowerCase());
  const off = self || inherited;
  // Eager nodes know their children; lazy nodes might have some until proven empty.
  const mayHaveKids = loaded ? loaded.length > 0 : !!node.loadChildren;

  const toggleOpen = async () => {
    if (!mayHaveKids) return;
    if (!open && !loaded && node.loadChildren) {
      setLoading(true);
      try {
        setLoaded(await node.loadChildren());
      } catch {
        setLoaded([]);
      } finally {
        setLoading(false);
      }
    }
    setOpen((o) => !o);
  };

  return (
    <>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 6,
          padding: "3px 6px",
          paddingLeft: 6 + depth * 18,
          borderRadius: 4,
          color: off ? "#9ca3af" : "var(--theme-ink)",
          textDecoration: self ? "line-through" : "none",
        }}
      >
        <span
          onClick={toggleOpen}
          style={{ width: 14, textAlign: "center", cursor: mayHaveKids ? "pointer" : "default", userSelect: "none", color: "#6b7280" }}
        >
          {loading ? "…" : mayHaveKids ? (open ? "▾" : "▸") : ""}
        </span>
        <input
          type="checkbox"
          checked={off}
          disabled={inherited}
          onChange={() => onToggle(node.path)}
          title={inherited ? "Excluded by parent folder" : self ? "Include this folder" : "Exclude this folder"}
          style={{ margin: 0, cursor: inherited ? "not-allowed" : "pointer" }}
        />
        <span onClick={toggleOpen} style={{ cursor: mayHaveKids ? "pointer" : "default", userSelect: "none" }}>
          📁 {node.name}
        </span>
        {loaded && loaded.length > 0 && <span style={{ color: "#9ca3af", fontSize: 11 }}>({loaded.length})</span>}
        {!off && isNewFolder?.(node.path) && (
          <span style={{ fontSize: 10, background: "#fef3c7", color: "#92400e", borderRadius: 4, padding: "0 5px", fontWeight: 600 }}>
            new
          </span>
        )}
      </div>
      {open &&
        loaded?.map((c) => (
          <Row key={c.path} node={c} depth={depth + 1} excluded={excluded} inherited={off} onToggle={onToggle} isNewFolder={isNewFolder} />
        ))}
    </>
  );
}
